import { useIntl } from 'react-intl';
import type { FormatNumberOptions } from 'react-intl';

import type { SelectOption } from '@swf/components/Inputs/components/DropDown/DropDown';
import { Currency } from '@swf/core/enums';
import type { TableParams } from '@swf/generated/types/api/v0/SwHomeGames';

import type { FormValues } from '../utils/common';
import { fallback } from '../utils/common';

export interface BuyIn {
  minBuyIn: number;
  maxBuyIn: number;
  stakesLow: number;
  stakesHigh: number;
}

const useGetStakes = (gameData: TableParams[], values: FormValues): SelectOption[] => {
  const { formatNumber } = useIntl();

  if (!gameData || !gameData.length) return fallback;

  const { handType: handTypeValue, potStruct, tableSize, currency } = values;
  const { handType, hiLo } = JSON.parse(handTypeValue);
  const isPlayMoney = currency === Currency.PlayMoney;

  const data = gameData
    .filter(
      (item) =>
        item.handType === handType &&
        item.hiLo === hiLo &&
        item.potStruct === potStruct &&
        item.tableSize === tableSize &&
        (item.currency === currency || (item.currency === '' && isPlayMoney))
    )
    .sort((a, b) => {
      if (!a.stakesLow || !b.stakesLow || !a.scalePM || !b.scalePM) return 0;
      if (isPlayMoney) return a.stakesLow * a.scalePM - b.stakesLow * b.scalePM;
      return a.stakesLow - b.stakesLow;
    });

  const formatOptionsRM: FormatNumberOptions = {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    currencySign: 'standard',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  };

  const options = data.map(({ minBuyIn, maxBuyIn, stakesLow, stakesHigh, scalePM }, index) => {
    if (!minBuyIn || !maxBuyIn || !stakesLow || !stakesHigh || !scalePM)
      return { text: '', value: '' };
    const stakesLowCalc = isPlayMoney
      ? formatNumber(stakesLow * scalePM * 0.01)
      : formatNumber(stakesLow * 0.01, { ...formatOptionsRM });
    const stakesHighCalc = isPlayMoney
      ? formatNumber(stakesHigh * scalePM * 0.01)
      : formatNumber(stakesHigh * 0.01, { ...formatOptionsRM });

    return {
      text: `${stakesLowCalc}/${stakesHighCalc}`,
      value: JSON.stringify({ id: index, minBuyIn, maxBuyIn, stakesLow, stakesHigh, scalePM })
    };
  });

  if (!options || !options.length) return fallback;

  return options;
};

export default useGetStakes;
